// src/admin/pages/UserInvitesAdminPage.js
// Admin page for managing User Invites settings and configuration

import React, { useState, useEffect } from 'react';
import { 
  Save,
  AlertCircle,
  Mail,
  Clock,
  CheckCircle
} from 'lucide-react';
import { supabase } from '../../config';

const UserInvitesAdminPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load settings from Supabase
  const loadSettings = async () => {
    setLoading(true);
    try {
      // Fetch platform settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      if (!settingsError && settingsData) {
        setSettings(settingsData.settings || {});
      } else {
        // Default settings
        setSettings({
          invite_default_expiry_days: 7,
          invite_default_role: 'viewer',
          invite_auto_reminders: true,
          invite_reminder_days: 3,
          invite_require_email_confirmation: true,
          invite_from_name: 'Kapitalized'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Get current user for updated_by
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get existing settings or create new
      const { data: existingSettings, error: fetchError } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const settingsData = {
        settings: settings,
        updated_at: new Date().toISOString(),
        updated_by: user?.id || null
      };

      if (existingSettings) {
        // Update existing
        const { error: updateError } = await supabase
          .from('platform_settings')
          .update(settingsData)
          .eq('id', existingSettings.id);

        if (updateError) throw updateError;
      } else {
        // Insert new
        const { error: insertError } = await supabase
          .from('platform_settings')
          .insert({ id: 1, ...settingsData });

        if (insertError) throw insertError;
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Invites Administration</h1>
          <p className="text-gray-600 mt-2">Configure global settings for user invitations</p>
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' 
            ? <CheckCircle className="w-5 h-5 mr-2" />
            : <AlertCircle className="w-5 h-5 mr-2" />
          }
          {message.text}
        </div>
      )}

      {/* Settings Content */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Settings</h3>
          <p className="text-sm text-gray-600 mb-6">
            Configure global settings for user invitations. These settings apply to all invitations sent from the platform.
          </p>

          {/* General Settings */}
          <div className="border-b pb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="h-5 w-5 mr-2 text-blue-600" />
              General Settings
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Default Expiry Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Invitation Expiry (Days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.invite_default_expiry_days || 7}
                  onChange={(e) => updateSetting('invite_default_expiry_days', parseInt(e.target.value) || 7)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Invitations expire after this many days</p>
              </div>

              {/* Default Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Role for New Invites
                </label>
                <select
                  value={settings.invite_default_role || 'viewer'}
                  onChange={(e) => updateSetting('invite_default_role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="viewer">Viewer</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Users can only be invited as Viewers. Creator role requires a paid subscription.</p>
              </div>

              {/* Email From Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email From Name
                </label>
                <input
                  type="text"
                  value={settings.invite_from_name || 'Kapitalized'}
                  onChange={(e) => updateSetting('invite_from_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Kapitalized"
                />
                <p className="mt-1 text-xs text-gray-500">This name will appear as the sender in all invitation emails</p>
              </div>
            </div>
          </div>

          {/* Email Settings */}
          <div className="border-b pb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-green-600" />
              Email Settings
            </h4>

            <div className="space-y-4">
              {/* Auto Reminders */}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.invite_auto_reminders !== false}
                  onChange={(e) => updateSetting('invite_auto_reminders', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Send Automatic Reminders
                </span>
              </label>
              <p className="ml-7 text-xs text-gray-500">Automatically remind users about pending invitations</p>

              {/* Reminder Days */}
              {settings.invite_auto_reminders !== false && (
                <div className="ml-7">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send Reminder After (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={settings.invite_reminder_days || 3}
                    onChange={(e) => updateSetting('invite_reminder_days', parseInt(e.target.value) || 3)}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Require Email Confirmation */}
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.invite_require_email_confirmation !== false}
                  onChange={(e) => updateSetting('invite_require_email_confirmation', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Require Email Confirmation
                </span>
              </label>
              <p className="ml-7 text-xs text-gray-500">Users must confirm their email before accessing content</p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Note:</p>
                <p>These settings apply globally to all invitations. Email templates are stored in:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li><strong>File System:</strong> <code className="bg-blue-100 px-1 rounded">api/email_templates/</code> (default templates)</li>
                  <li><strong>Database:</strong> <code className="bg-blue-100 px-1 rounded">userinvites_templates</code> table (custom templates)</li>
                </ul>
                <p className="mt-2">Manage email templates in <strong>AdminHQ → Email Templates</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInvitesAdminPage;
